import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';

type MessageHandler = (data: any) => void;

interface WebSocketContextType {
  ws: WebSocket | null;
  isConnected: boolean;
  reconnect: () => void;
  addMessageHandler: (handler: MessageHandler) => void;
  removeMessageHandler: (handler: MessageHandler) => void;
}

export const WebSocketContext = createContext<WebSocketContextType>({
  ws: null,
  isConnected: false,
  reconnect: () => {},
  addMessageHandler: () => {},
  removeMessageHandler: () => {},
});

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context.ws;
};

export const useWebSocketContext = () => {
  return useContext(WebSocketContext);
};

interface WebSocketProviderProps {
  children: ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const messageHandlersRef = useRef<Set<MessageHandler>>(new Set());
  const maxReconnectAttempts = 10;
  const reconnectDelay = 3000;

  // Message handler yönetimi
  const addMessageHandler = (handler: MessageHandler) => {
    messageHandlersRef.current.add(handler);
  };

  const removeMessageHandler = (handler: MessageHandler) => {
    messageHandlersRef.current.delete(handler);
  };

  const connect = () => {
    const token = localStorage.getItem('token');

    if (!token) {
      return;
    }

    // Mevcut bağlantıyı kapat
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = `${import.meta.env.VITE_WS_URL}/admin?token=${token}`;

    const websocket = new WebSocket(wsUrl);
    wsRef.current = websocket;

    websocket.onopen = () => {
      setIsConnected(true);
      setWs(websocket);
      reconnectAttemptsRef.current = 0;
    };

    websocket.onerror = (error) => {
      console.error('❌ WebSocket hatası:', error);
      setIsConnected(false);
    };

    websocket.onclose = () => {
      setIsConnected(false);
      setWs(null);
      wsRef.current = null;

      // Otomatik yeniden bağlanma
      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current++;

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay);
      }
    };

    websocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        // Tüm handler'lara mesajı ilet
        messageHandlersRef.current.forEach(handler => {
          try {
            handler(message);
          } catch (handlerError) {
            console.error('❌ [WebSocketProvider] Handler hatası:', handlerError);
          }
        });
      } catch (error) {
        console.error('❌ [WebSocketProvider] Mesaj parse hatası:', error);
      }
    };
  };

  const reconnect = () => {
    reconnectAttemptsRef.current = 0;
    connect();
  };

  useEffect(() => {
    // Token varsa bağlan
    const token = localStorage.getItem('token');
    if (token) {
      connect();
    }

    // Cleanup
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  return (
    <WebSocketContext.Provider value={{ ws, isConnected, reconnect, addMessageHandler, removeMessageHandler }}>
      {children}
    </WebSocketContext.Provider>
  );
};
